import { COUNTRY } from 'common/constants';
import { FormValidator } from 'common/Validator/Validator';

import { INSURANCE_FORM_VALIDATOR_CONFIG, PATIENT_INFO_VALIDATOR_CONFIG } from './Insurance/form-validator-configs';

import { CheckoutStepDefinition, CheckoutStepParams, StepId } from './types';

const isCanadianClinic = (clinic: any) => clinic?.address?.country === COUNTRY.canada;

const isEmailVerified = ({ isEmailVerificationSkipped, patient }: CheckoutStepParams) => {
  if (isEmailVerificationSkipped) {
    return true;
  }

  return patient?.emailVerifiedAt;
};

/**
 * The ordered list of steps in the checkout process.
 * This should not be used directly to determine the current step.
 * Instead, use the `useCurrentCheckoutStep` hook to get the current step.
 */
export const stepDefinitions: CheckoutStepDefinition[] = [
  {
    id: StepId.ACCOUNT,
    name: 'Info',
    path: 'account',
    showInProgress: true,
    isApplicable: () => true,
    isComplete: ({ isUserAuthenticated }) => isUserAuthenticated,
  },
  { // Used if the user doesn't have a phone number associated with their account
    id: StepId.PHONE_NUMBER,
    name: 'Phone Number',
    path: 'phone-number',
    showInProgress: false,
    isApplicable: ({ patient }) => !patient?.mobilePhone,
    isComplete: ({ patient }) => Boolean(patient?.mobilePhone),
  },
  {
    id: StepId.PATIENT_INFO,
    name: 'Insurance',
    path: 'patient',
    reduxKey: 'patient',
    showInProgress: true,
    isApplicable: () => true,
    isComplete: ({ checkoutState }) => {
      const { firstName, lastName, dateOfBirth, sexAssignedAtBirth } = checkoutState.patient;
      const { day, month, year } = dateOfBirth;
      const flattenedFields = { firstName, lastName, sexAssignedAtBirth, dateOfBirth: { day, month, year } };

      const patientFormValidator = new FormValidator(PATIENT_INFO_VALIDATOR_CONFIG);
      const validationResult = patientFormValidator.validate(flattenedFields, { showReturningPatientToggle: false });
      return validationResult.allValid;
    },
  },
  {
    id: StepId.INSURANCE,
    name: 'Insurance',
    path: 'insurance',
    reduxKey: 'insurance',
    showInProgress: false,
    isApplicable: ({ match }) => match?.other?.insurance?.hasInsurance !== 'No',
    isComplete: ({ checkoutState, clinic, match }) => {
      const isUsClinic = !isCanadianClinic(clinic);
      const insuranceFormValidator = new FormValidator(INSURANCE_FORM_VALIDATOR_CONFIG);

      const { hasInsurance, provider, plan, isPrimaryMember, primaryMember } = checkoutState.insurance;

      const insuranceFields = {
        hasInsurance,
        insuranceProvider: provider,
        insurancePlan: plan,
        isPrimaryMember,
        primaryMemberFirstName: primaryMember?.firstName,
        primaryMemberLastName: primaryMember?.lastName,
        primaryMemberDateOfBirth: primaryMember?.dateOfBirth,
      };

      const showHasInsuranceQuestion = match?.other?.hasInsurance === null;

      const params = {
        isUsClinic,
        isPrimaryMember: Boolean(isPrimaryMember),
        showHasInsuranceQuestion,
        hasSelectedNoInsurance: !hasInsurance,
      };
      const insuranceValidationResult = insuranceFormValidator.validate(insuranceFields, params);
      return insuranceValidationResult.allValid;
    },
  },
  {
    id: StepId.VERIFY,
    name: 'Verify',
    path: 'verify',
    reduxKey: 'verify',
    showInProgress: false,
    isApplicable: (params) => !isEmailVerified(params) || !params.patient?.phoneVerifiedAt,
    isComplete: (params) => isEmailVerified(params)
      && params.patient?.phoneVerifiedAt
      && !params.patient?.unverifiedPhoneData,
  },
  {
    id: StepId.CONFIRM,
    name: 'Confirm',
    path: 'confirm',
    reduxKey: 'confirm',
    showInProgress: true,
    isApplicable: () => true,
    isComplete: () => false,
  },
];
